import type { TeamSessionSummaryResponse } from '@frontend/core/lib/domains/reporting/types'
import { formatStatValue } from '../lib/format-cell'
import type { ReportingSportConfig } from '../lib/sport-config'
import type { DashboardChartMetricDef } from '../lib/sport-config/types'

function safeGetValue(
  metric: unknown,
  key: 'value' | 'seasonAvg' | 'maxValue' | 'minValue'
): number | null {
  if (metric == null) return null
  if (typeof metric === 'number') return metric
  if (typeof metric === 'object' && key in metric) {
    const val = (metric as Record<string, unknown>)[key]
    return typeof val === 'number' ? val : null
  }
  return null
}

/**
 * Normalise value against min/max range (matching Angular PerformanceBarUtils.fromSeasonAvg).
 */
function normaliseToRange(
  value: number,
  minValue: number,
  maxValue: number
): number {
  const valueRange = maxValue - minValue
  if (valueRange === 0) return 100
  const paddedMin = Math.max(0, minValue - valueRange * 0.01)
  const normalizedMax = maxValue - paddedMin
  const normalizedValue = value - paddedMin
  return Math.min(100, Math.max(0, (normalizedValue / normalizedMax) * 100))
}

export interface ChartMetric {
  name: string
  unit?: string
  displayValue: string
  barHeight: number
  refMarkerPos: number | null
  level: 'green' | 'orange' | 'red'
  sessionValue: string
  seasonMaxValue: string
  seasonAvgValue: string
}

export function buildMetrics(
  m: TeamSessionSummaryResponse['summaryMetrics'],
  metricDefs: DashboardChartMetricDef[],
  config: ReportingSportConfig
): ChartMetric[] {
  const seen = new Set<string>()
  return metricDefs
    .filter((def) => {
      if (seen.has(def.key)) return false
      const metric = (m as Record<string, unknown>)[def.key]
      if (metric == null) return false
      seen.add(def.key)
      return true
    })
    .map((def) => {
      const metric = (m as Record<string, unknown>)[def.key]
      const value = safeGetValue(metric, 'value')
      const seasonAvg = safeGetValue(metric, 'seasonAvg')
      const maxValue = safeGetValue(metric, 'maxValue') ?? def.fallbackMax
      const minValue = safeGetValue(metric, 'minValue') ?? 0

      let barHeight = 0
      let refMarkerPos: number | null = null
      let level: 'green' | 'orange' | 'red' = 'green'

      if (value != null && maxValue > 0) {
        barHeight = normaliseToRange(value, minValue, maxValue)
        if (seasonAvg != null) {
          refMarkerPos = normaliseToRange(seasonAvg, minValue, maxValue)
          if (value >= seasonAvg) level = 'green'
          else if (value >= seasonAvg * 0.9) level = 'orange'
          else level = 'red'
        }
      }

      // Single formatting rule via `formatStatValue`: `count` → flat integer,
      // distance/speed → convert to the active unit + 1dp, everything else
      // (percentages included) → 1dp. Only distance/speed carry a unit label.
      const unitLabel =
        def.unitType === 'distance'
          ? config.distanceUnit
          : def.unitType === 'speed'
            ? config.speedUnit
            : undefined
      const fmt = (v: number) =>
        formatStatValue(v, def.unitType, config.unitSystem)

      return {
        name: unitLabel ? `${def.name} (${unitLabel})` : def.name,
        unit: unitLabel,
        displayValue: value != null ? fmt(value) : '-',
        barHeight,
        refMarkerPos,
        level,
        sessionValue: value != null ? fmt(value) : '-',
        seasonMaxValue: maxValue != null ? fmt(maxValue) : '-',
        seasonAvgValue: seasonAvg != null ? fmt(seasonAvg) : '-'
      }
    })
}
